import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const LOGO = "/deck/assets/a-001-1c393ab857cb.png";

/**
 * /onboard/book — standalone booking page.
 * No payment required, no session — just email + name + phone → pick a time → GHL appt.
 */
export default function PublicBookingPage() {
  const [step, setStep] = useState("who"); // "who" | "pick" | "done"
  const [availability, setAvailability] = useState(null);
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(null);

  // Prefill from URL query params
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const e = p.get("email"); if (e) setEmail(e);
      const f = p.get("first_name") || p.get("first"); if (f) setFirstName(f);
      const l = p.get("last_name") || p.get("last"); if (l) setLastName(l);
      const ph = p.get("phone"); if (ph) setPhone(ph);
    } catch {}
  }, []);

  // Load availability once we get to pick step
  useEffect(() => {
    if (step !== "pick" || availability) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/onboard/availability?days=14`);
        setAvailability(res.data);
        const dates = Object.keys(res.data.slots_by_date || {}).sort();
        if (dates[0]) setPickedDate(dates[0]);
      } catch { setErr("Could not load available times. Please refresh."); }
    })();
  }, [step, availability]);

  const goPick = (e) => {
    e.preventDefault();
    if (!email) return;
    setErr("");
    setStep("pick");
  };

  const book = async () => {
    if (!pickedSlot || busy) return;
    setBusy(true);
    setErr("");
    try {
      const res = await axios.post(`${API}/onboard/book-public`, {
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        start_time: pickedSlot,
        notes: notes.trim(),
      });
      setConfirmed(res.data);
      setStep("done");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not book that time. Try another.");
    }
    setBusy(false);
  };

  return (
    <div style={S.page}>
      <div style={S.bgGlow} />
      <header style={S.header}>
        <img src={LOGO} alt="Skye AI" style={S.logo} />
        <span style={S.badge}>BOOK A CALL</span>
      </header>
      <main style={S.main}>
        {step === "who" && (
          <form style={S.card} onSubmit={goPick} data-testid="book-who-form">
            <div style={S.eyebrow}>Step 1 · Who are you</div>
            <h1 style={S.h1}>Let&rsquo;s find a time</h1>
            <p style={S.sub}>Quick 30-minute call — we&rsquo;ll walk you through what Skye can do for your business.</p>

            <div style={S.field}>
              <label style={S.label}>Your email</label>
              <input type="email" required autoFocus inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourbusiness.com" style={S.input} data-testid="book-email-input" />
            </div>
            <div style={S.row}>
              <div style={{ ...S.field, flex: 1 }}>
                <label style={S.label}>First name</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First" style={S.input} data-testid="book-first-input" />
              </div>
              <div style={{ ...S.field, flex: 1 }}>
                <label style={S.label}>Last name</label>
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last" style={S.input} data-testid="book-last-input" />
              </div>
            </div>
            <div style={S.field}>
              <label style={S.label}>Phone (optional)</label>
              <input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" style={S.input} data-testid="book-phone-input" />
            </div>
            <div style={S.field}>
              <label style={S.label}>What do you want to cover? (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="A quick sentence helps us prep" style={{ ...S.input, minHeight: 72, resize: "vertical", fontFamily: "inherit" }} data-testid="book-notes-input" />
            </div>

            <button type="submit" disabled={!email} style={{ ...S.primaryBtn, opacity: email ? 1 : 0.45 }} data-testid="book-next-btn">
              See open times →
            </button>
          </form>
        )}

        {step === "pick" && (
          <div style={S.card} data-testid="book-pick">
            <div style={S.eyebrow}>Step 2 · Pick a time</div>
            <h2 style={S.h2}>{firstName ? `Great, ${firstName} — pick a time` : "Pick a time that works"}</h2>
            <p style={S.sub}>30 minutes · {availability?.timezone || "loading"} time</p>

            {!availability ? (
              <div style={S.pendingBox}>
                <div style={S.pendingSpinner} />
                <div>
                  <div style={S.pendingTitle}>Loading open times…</div>
                  <div style={S.pendingSub}>One sec.</div>
                </div>
              </div>
            ) : (() => {
              const dates = Object.keys(availability.slots_by_date || {}).sort();
              const slotsForDate = pickedDate ? (availability.slots_by_date[pickedDate] || []) : [];
              if (dates.length === 0) {
                return <div style={S.info}>No open times in the next 14 days. Email us and we&rsquo;ll find one.</div>;
              }
              return (
                <>
                  <div style={S.dateScroller}>
                    {dates.map((d) => {
                      const [y, m, day] = d.split("-");
                      const dateObj = new Date(+y, +m - 1, +day);
                      const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
                      const monthDay = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                      const active = d === pickedDate;
                      return (
                        <button key={d} type="button" onClick={() => { setPickedDate(d); setPickedSlot(null); }} style={{ ...S.dateChip, ...(active ? S.dateChipActive : {}) }} data-testid={`book-date-${d}`}>
                          <span style={S.dateChipDay}>{weekday}</span>
                          <span style={S.dateChipDate}>{monthDay}</span>
                          <span style={S.dateChipCount}>{(availability.slots_by_date[d] || []).length} open</span>
                        </button>
                      );
                    })}
                  </div>
                  <div style={S.slotGrid}>
                    {slotsForDate.length === 0 && <div style={S.info}>No open times that day. Try another date.</div>}
                    {slotsForDate.map((s) => {
                      const label = new Date(s).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                      const active = s === pickedSlot;
                      return <button key={s} type="button" onClick={() => setPickedSlot(s)} style={{ ...S.slotBtn, ...(active ? S.slotBtnActive : {}) }} data-testid={`book-slot-${s}`}>{label}</button>;
                    })}
                  </div>
                  {err && <div style={S.error}>{err}</div>}
                  <button onClick={book} disabled={!pickedSlot || busy} style={{ ...S.primaryBtn, opacity: pickedSlot && !busy ? 1 : 0.4 }} data-testid="book-confirm-btn">
                    {busy ? "Booking…" : pickedSlot
                      ? `Book ${new Date(pickedSlot).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
                      : "Pick a time above"}
                  </button>
                  <button type="button" onClick={() => setStep("who")} style={S.secondaryBtn} data-testid="book-back-btn">← Change my info</button>
                </>
              );
            })()}
          </div>
        )}

        {step === "done" && confirmed && (
          <div style={S.card} data-testid="book-done">
            <div style={S.completeBadge}>✓</div>
            <h1 style={S.h1}>You&rsquo;re booked, {firstName || "friend"}</h1>
            <p style={S.sub}>Check {email} — we sent the call link and a calendar invite.</p>
            <div style={S.summaryGrid}>
              <div style={S.summaryRow}>
                <span>When</span>
                <b>{new Date(confirmed.start_time).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</b>
              </div>
              <div style={S.summaryRow}>
                <span>Length</span>
                <b>30 minutes</b>
              </div>
            </div>
            <p style={S.sub}>Need to reschedule? Reply to the calendar invite — we&rsquo;ll sort it.</p>
          </div>
        )}
      </main>
      <footer style={S.footer}>Need help? Text your rep · replies within minutes</footer>
    </div>
  );
}

// Styles — mirror the OnboardWizard vibe
const S = {
  page: { minHeight: "100vh", background: "#050508", color: "#fff", fontFamily: "'Manrope',system-ui,-apple-system,sans-serif", position: "relative", overflow: "hidden", paddingBottom: 40 },
  bgGlow: { position: "fixed", top: "-20%", left: "-10%", width: "60%", height: "60%", background: "radial-gradient(closest-side,rgba(157,50,243,.16),transparent)", pointerEvents: "none" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px", position: "relative", zIndex: 2 },
  logo: { height: 30 },
  badge: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.8, color: "#F472B6", background: "rgba(244,114,182,.08)", border: "1px solid rgba(244,114,182,.28)", padding: "5px 10px", borderRadius: 100 },
  main: { maxWidth: 480, margin: "0 auto", padding: "8px 16px 24px", position: "relative", zIndex: 2 },
  card: { background: "linear-gradient(180deg,rgba(255,255,255,.04),rgba(255,255,255,.02))", border: "1px solid rgba(255,255,255,.08)", borderRadius: 20, padding: "28px 22px 24px", boxShadow: "0 14px 40px -20px rgba(0,0,0,.5),inset 0 1px 0 rgba(255,255,255,.05)" },
  eyebrow: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.8, color: "rgba(255,255,255,.55)", textTransform: "uppercase", marginBottom: 10 },
  h1: { fontSize: 26, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-.02em", margin: "0 0 8px", color: "#fff" },
  h2: { fontSize: 22, fontWeight: 800, lineHeight: 1.25, letterSpacing: "-.015em", margin: "0 0 8px", color: "#fff" },
  sub: { fontSize: 14, fontWeight: 400, color: "rgba(255,255,255,.62)", lineHeight: 1.5, margin: "0 0 18px" },
  field: { marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 },
  label: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: "rgba(255,255,255,.55)", textTransform: "uppercase" },
  input: { padding: "13px 14px", fontSize: 15, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 12, color: "#fff", outline: "none", fontFamily: "inherit", transition: "border-color .18s ease,background .18s ease" },
  row: { display: "flex", gap: 10 },
  primaryBtn: { display: "block", width: "100%", padding: "15px 20px", marginTop: 12, background: "linear-gradient(135deg,#dc328c 0%,#9d32f3 100%)", color: "#fff", fontSize: 15, fontWeight: 800, letterSpacing: "-.005em", border: "none", borderRadius: 14, cursor: "pointer", boxShadow: "0 10px 26px -6px rgba(220,50,140,.5),inset 0 1px 0 rgba(255,255,255,.2)", textAlign: "center", textDecoration: "none", fontFamily: "inherit" },
  secondaryBtn: { display: "block", width: "100%", padding: "13px 18px", marginTop: 10, background: "rgba(255,255,255,.05)", color: "rgba(255,255,255,.85)", fontSize: 14, fontWeight: 700, border: "1px solid rgba(255,255,255,.15)", borderRadius: 12, cursor: "pointer", textDecoration: "none", textAlign: "center", fontFamily: "inherit" },
  error: { padding: "10px 12px", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 10, color: "#FCA5A5", fontSize: 13, margin: "8px 0" },
  info: { padding: "12px 14px", background: "rgba(90,178,255,.06)", border: "1px solid rgba(90,178,255,.22)", borderRadius: 10, color: "#93C5FD", fontSize: 13, margin: "8px 0" },
  footer: { textAlign: "center", fontSize: 11, color: "rgba(255,255,255,.35)", marginTop: 20, padding: "0 16px" },
  pendingBox: { display: "flex", alignItems: "center", gap: 14, padding: "16px 16px", background: "rgba(157,50,243,.06)", border: "1px solid rgba(157,50,243,.24)", borderRadius: 14, marginTop: 6 },
  pendingSpinner: { width: 22, height: 22, border: "2.5px solid rgba(157,50,243,.25)", borderTopColor: "#dc328c", borderRadius: "50%", flexShrink: 0, animation: "onbSpin 0.8s linear infinite" },
  pendingTitle: { fontSize: 14, fontWeight: 700, color: "#fff" },
  pendingSub: { fontSize: 12, color: "rgba(255,255,255,.55)", marginTop: 2 },
  dateScroller: { display: "flex", gap: 8, overflowX: "auto", padding: "4px 2px 12px", marginLeft: -4, marginRight: -4, WebkitOverflowScrolling: "touch", scrollbarWidth: "none" },
  dateChip: { flexShrink: 0, minWidth: 68, padding: "10px 8px", background: "rgba(255,255,255,.03)", border: "1.5px solid rgba(255,255,255,.08)", borderRadius: 12, color: "rgba(255,255,255,.7)", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, transition: "all .18s ease" },
  dateChipActive: { background: "linear-gradient(180deg,rgba(220,50,140,.14),rgba(157,50,243,.06))", borderColor: "rgba(220,50,140,.5)", color: "#fff" },
  dateChipDay: { fontSize: 10, fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", opacity: .7 },
  dateChipDate: { fontSize: 15, fontWeight: 800 },
  dateChipCount: { fontSize: 9, fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, opacity: .5, letterSpacing: .4, marginTop: 2 },
  slotGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 },
  slotBtn: { padding: "12px 8px", background: "rgba(255,255,255,.03)", border: "1.5px solid rgba(255,255,255,.08)", borderRadius: 10, color: "rgba(255,255,255,.85)", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all .18s ease" },
  slotBtnActive: { background: "linear-gradient(135deg,#dc328c,#9d32f3)", borderColor: "transparent", color: "#fff", boxShadow: "0 6px 18px -4px rgba(220,50,140,.55)" },
  completeBadge: { width: 60, height: 60, borderRadius: "50%", background: "linear-gradient(135deg,#4ADE80,#22c55e)", color: "#fff", fontSize: 30, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 0 42px rgba(74,222,128,.4)" },
  summaryGrid: { display: "flex", flexDirection: "column", gap: 10, margin: "18px 0" },
  summaryRow: { display: "flex", justifyContent: "space-between", padding: "12px 14px", background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 10, fontSize: 13 },
};

if (typeof document !== "undefined" && !document.getElementById("book-kf")) {
  const s = document.createElement("style");
  s.id = "book-kf";
  s.textContent = "@keyframes onbSpin{to{transform:rotate(360deg)}}input::placeholder,textarea::placeholder{color:rgba(255,255,255,.35)}input:focus,textarea:focus{border-color:rgba(220,50,140,.55) !important;background:rgba(255,255,255,.06) !important}button:not(:disabled):hover{transform:translateY(-1px)}::-webkit-scrollbar{display:none}";
  document.head.appendChild(s);
}
